/**
 * Waritto-specific skeleton loading components
 * Matches the actual card designs for better perceived performance
 */

import { cn } from "@tabipla/ui";

interface SkeletonProps {
  className?: string;
}

/**
 * Base skeleton component for custom skeleton layouts
 */
export function Skeleton({ className }: SkeletonProps) {
  return <div className={cn("animate-shimmer rounded bg-muted", className)} />;
}

/**
 * Skeleton for group cards in the groups list
 * Matches the GroupCard design with icon, title, description, and balance
 */
export function GroupCardSkeleton() {
  return (
    <div className="overflow-hidden rounded-xl border bg-card">
      <div className="flex items-center gap-3 p-4">
        {/* Group Icon */}
        <Skeleton className="h-12 w-12 shrink-0 rounded-xl" />

        {/* Group Info */}
        <div className="min-w-0 flex-1 space-y-2">
          <Skeleton className="h-5 w-3/4" />
          <div className="flex items-center gap-2">
            <Skeleton className="h-4 w-16" />
            <Skeleton className="h-5 w-20 rounded-full" />
          </div>
        </div>

        {/* Balance */}
        <div className="shrink-0 space-y-1 text-right">
          <Skeleton className="ml-auto h-6 w-20" />
          <Skeleton className="ml-auto h-3 w-12" />
        </div>

        {/* Chevron */}
        <Skeleton className="h-5 w-5 shrink-0 rounded" />
      </div>
    </div>
  );
}

/**
 * Skeleton for expense cards in the expense list
 */
export function ExpenseCardSkeleton() {
  return (
    <div className="overflow-hidden rounded-xl border bg-card">
      <div className="flex items-center gap-3 p-4">
        {/* Category Icon */}
        <Skeleton className="h-12 w-12 shrink-0 rounded-xl" />

        {/* Expense Info */}
        <div className="min-w-0 flex-1 space-y-2">
          <Skeleton className="h-5 w-2/3" />
          <Skeleton className="h-6 w-24" />
          <div className="flex items-center gap-2">
            <Skeleton className="h-4 w-20" />
            <Skeleton className="h-4 w-16" />
          </div>
        </div>

        {/* Chevron */}
        <Skeleton className="h-5 w-5 shrink-0 rounded" />
      </div>
    </div>
  );
}

/**
 * Skeleton for settlement cards
 */
export function SettlementCardSkeleton() {
  return (
    <div className="overflow-hidden rounded-xl border bg-card p-4">
      <div className="flex items-center gap-3">
        {/* Avatar */}
        <Skeleton className="h-10 w-10 shrink-0 rounded-full" />

        {/* Arrow */}
        <Skeleton className="h-5 w-5 shrink-0" />

        {/* Avatar */}
        <Skeleton className="h-10 w-10 shrink-0 rounded-full" />

        {/* Amount */}
        <div className="ml-auto space-y-1 text-right">
          <Skeleton className="ml-auto h-6 w-20" />
          <Skeleton className="ml-auto h-5 w-16 rounded-full" />
        </div>
      </div>
    </div>
  );
}

/**
 * Loading skeleton list for groups page
 */
export function GroupsListSkeleton({ count = 3 }: { count?: number }) {
  return (
    <div className="space-y-3">
      {Array.from({ length: count }).map((_, i) => (
        <GroupCardSkeleton key={i} />
      ))}
    </div>
  );
}

/**
 * Loading skeleton list for expenses page
 */
export function ExpensesListSkeleton({ count = 5 }: { count?: number }) {
  return (
    <div className="space-y-3">
      {Array.from({ length: count }).map((_, i) => (
        <ExpenseCardSkeleton key={i} />
      ))}
    </div>
  );
}

/**
 * Loading skeleton list for settlements
 */
export function SettlementsListSkeleton({ count = 3 }: { count?: number }) {
  return (
    <div className="space-y-3">
      {Array.from({ length: count }).map((_, i) => (
        <SettlementCardSkeleton key={i} />
      ))}
    </div>
  );
}
